import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { toast } from 'sonner';
import { toApiError, useApi, type BookableResourceResponse } from '@/shared/api';
import { resourceRequest, type ResourceValues } from './resourceModel';
import { resourceFailureText } from './resourceProblemText';

export const resourcesQueryKey = (groupId: string) => ['property-groups', groupId, 'resources'];

export function useResources(groupId: string | undefined, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: resourcesQueryKey(groupId ?? ''),
    queryFn: () => api.resources.list(groupId!),
    enabled: enabled && Boolean(groupId),
  });
}

/**
 * Legt an oder ändert. Eine Ablehnung bleibt als Satz im Sheet stehen; ohne Antwort ist offen, ob
 * gespeichert wurde — dann lädt die Liste neu, und ein zweites Anlegen scheitert am eindeutigen
 * Namen, statt eine Kopie zu erzeugen.
 */
export function useResourceSave(
  groupId: string,
  resource: BookableResourceResponse | undefined,
  currency: string,
  onDone: () => void,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (values: ResourceValues) => {
      const body = resourceRequest(values, currency);
      return resource
        ? api.resources.update(groupId, resource.id, body)
        : api.resources.create(groupId, body);
    },
    networkMode: 'always',
    onMutate: () => setError(null),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: resourcesQueryKey(groupId) });
      toast.success(resource ? `„${saved.name}“ gespeichert` : `„${saved.name}“ angelegt`);
      onDone();
    },
    onError: async (failure, values) => {
      const problem = await toApiError(failure);
      if (problem.status !== 0) {
        setError(resourceFailureText(problem));
        return;
      }
      if (!(failure instanceof HTTPError) && !(failure instanceof TypeError))
        console.error(failure);
      setError(resourceFailureText(problem));
      const reloaded = await queryClient
        .fetchQuery({
          queryKey: resourcesQueryKey(groupId),
          queryFn: () => api.resources.list(groupId),
          staleTime: 0,
        })
        .catch(() => null);
      // Beim Anlegen klärt die neu geladene Liste, ob die Antwort nur verloren ging: steht der
      // Name jetzt dort, wurde gespeichert — ein zweiter Klick liefe sonst in „Name vergeben“.
      const name = resourceRequest(values, currency).name.toLocaleUpperCase('de');
      if (
        !resource &&
        reloaded?.resources.some((item) => item.name.toLocaleUpperCase('de') === name)
      ) {
        toast.success(`„${values.name.trim()}“ angelegt`);
        onDone();
      }
    },
  });

  return { save: mutation.mutate, saving: mutation.isPending, error };
}
